import api, { API_BASE_URL } from './api';
import { ensureFreshToken } from './tokenManager';

/**
 * Portfolio Service
 * Handles all portfolio-related API calls
 */
export const portfolioService = {
  /**
   * Get all portfolios for current user
   * @returns {Promise<Array>} - List of portfolios
   */
  async getPortfolios() {
    return api.get('/investor/portfolios');
  },

  /**
   * Get all portfolios for a user by email
   * @param {string} email - User email
   * @returns {Promise<Array>} - List of portfolios
   */
  async getAllPortfoliosByEmail(email) {
    const params = new URLSearchParams({ email });
    return api.get(`/portfolio/all/email?${params.toString()}`);
  },

  /**
   * Get all portfolios for a user
   * @param {string} userId - User ID
   * @returns {Promise<Array>} - List of portfolios
   */
  async getAllPortfoliosByUserId(userId) {
    const params = new URLSearchParams({ userId });
    return api.get(`/portfolio/all/userid?${params.toString()}`);
  },

  async getAllPortfoliosByUserIdForInvestor(userId) {
    return api.get(`/portfolio/investor/${userId}`);
  },

  async getAllPortfoliosForClients(userId) {
    return api.get(`/portfolio/advisor/clients/${userId}`);
  },

  /**
   * Get portfolio by ID
   * @param {string} portfolioId - Portfolio ID
   * @returns {Promise<Object>} - Portfolio details
   */
  async getPortfolio(portfolioId) {
    return api.get(`/investor/portfolios/${portfolioId}`);
  },

  /**
   * Create new portfolio
   * @param {string} userId - User ID
   * @param {Object} portfolioData - Portfolio data
   * @returns {Promise<Object>} - Created portfolio
   */
  async createPortfolio(userId, portfolioData) {
    return api.post(`/portfolio/investor/${userId}`, portfolioData);
  },

  /**
   * Update portfolio
   * @param {string} portfolioId - Portfolio ID
   * @param {Object} portfolioData - Updated data
   * @returns {Promise<Object>} - Updated portfolio
   */
  async updatePortfolio(portfolioId, portfolioData) {
    return api.put(`/portfolio/investor/${portfolioId}`, portfolioData);
  },

  /**
   * Delete portfolio
   * @param {string} portfolioId - Portfolio ID
   * @returns {Promise<Object>} - Success response
   */
  async deletePortfolio(portfolioId) {
    return api.delete(`/portfolios/investor/${portfolioId}`);
  },

  /**
   * Get portfolio holdings
   * @param {string} portfolioId - Portfolio ID
   * @returns {Promise<Array>} - List of holdings
   */
  async getHoldings(portfolioId) {
    return api.get(`/portfolios/investor/${portfolioId}/holdings`);
  },

  /**
   * Get portfolio performance metrics
   * @param {string} portfolioId - Portfolio ID
   * @param {string} period - Time period (1D, 1W, 1M, 3M, 1Y, ALL)
   * @returns {Promise<Object>} - Performance data
   */
  async getPerformance(portfolioId, period = '1M') {
    return api.get(`/portfolios/investor/${portfolioId}/performance?period=${period}`);
  },

  /**
   * Get asset allocation
   * @param {string} portfolioId - Portfolio ID
   * @returns {Promise<Object>} - Asset allocation data
   */
  async getAssetAllocation(portfolioId) {
    return api.get(`/portfolios/investor/${portfolioId}/allocation`);
  },

  /**
   * Upload portfolio CSV
   * @param {File} file - CSV file
   * @returns {Promise<Object>} - Upload response
   */
  async uploadPortfolio(file) {
    const formData = new FormData();
    formData.append('file', file);
    
    return api.post('/portfolios/investor/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  /**
   * Update a position within a portfolio
   * @param {string} portfolioId - Portfolio ID
   * @param {string} symbol - Position symbol
   * @param {Object} positionData - Updated position data
   * @returns {Promise<Object>} - Updated position
   */
  async updatePortfolioPosition(portfolioId, positionData) {
    return api.put(`/portfolio/investor/${portfolioId}/position`, positionData);
  },

  /**
   * Delete a position from a portfolio
   * @param {string} portfolioId - Portfolio ID
   * @param {string} positionId - Position ID
   * @returns {Promise<Object>} - Success response
   */
  async deletePortfolioPosition(portfolioId, positionId) {
    return api.delete(`/portfolio/investor/${portfolioId}/position/${positionId}`);
  },

  /**
   * Create an account statement
   * @param {Object} dto - Account statement data
   * @returns {Promise<Object>} - Created account statement
   */
  async createAccountStatement(userId, dto) {
    return api.post(`/portfolio/investor/${userId}/statement`, dto);
  },

  /**
   * Update an account statement
   * @param {string} statementId - Statement ID
   * @param {Object} dto - Updated statement data
   * @returns {Promise<Object>} - Updated account statement
   */
  async updateAccountStatement(statementId, dto) {
    return api.put(`/portfolio/statement/${statementId}`, dto);
  },

  /**
   * Delete an account statement
   * @param {string} statementId - Statement ID
   * @returns {Promise<Object>} - Success response
   */
  async deleteAccountStatement(statementId) {
    return api.delete(`/portfolio/statement/${statementId}`);
  },

  /**
   * Get all account statements
   * @param {string} userId - User ID
   * @param {Object} request - Account statement request
   * @returns {Promise<Array>} - List of account statements
   */
  async getAllAccountStatements(userId, request) {
    const params = new URLSearchParams( request);
    return api.get(`/portfolio/${userId}/statement/all?${params.toString()}`);
  },

  /**
   * Delete an account statement
   * @param {string} statementId - Statement ID
   * @returns {Promise<Object>} - Success response
   */
  async deleteAccountStatement(statementId) {
    return api.delete(`/portfolio/statement/${statementId}`);
  },

  /**
   * Update an account statement
   * @param {string} statementId - Statement ID
   * @param {Object} dto - Updated statement data
   * @returns {Promise<Object>} - Updated account statement
   */
  async updateAccountStatement(statementId, dto) {
    return api.put(`/portfolio/statement/${statementId}`, dto);
  },

   /**
    * Get all account statements
    *@param {Object} request - bechmark monthly return request
    * @returns {Promise<Array>} - List of Benchmark monthly return
    */
  async getMonthlyBenchmarkReturn(request) {
    const params = new URLSearchParams(request);
    return api.get(`/portfolio/benchmark/monthly-return?${params.toString()}`);
  },

  /**
   * Get portfolio snapshot
   * @param {string} portfolioId - Portfolio ID
   * @param {string} date - Snapshot date (YYYY-MM-DD)
   * @returns {Promise<Object>} - Portfolio snapshot
   */
  async getSnapshot(portfolioId, date) {
    return api.get(`/portfolios/investor/${portfolioId}/snapshots/${date}`);
  },

  /**
   * Get all snapshots for portfolio
   * @param {string} portfolioId - Portfolio ID
   * @returns {Promise<Array>} - List of snapshots
   */
  async getSnapshots(portfolioId) {
    return api.get(`/portfolios/investor/${portfolioId}/snapshots`);
  },

  // ==================== Plaid Aggregation ====================
  // These call the backend contract documented in docs/plaid-architecture.md.
  // The backend owns the Plaid secret and access tokens; the browser never sees them.

  /**
   * Create a Plaid Link token to open Link for a user.
   * @param {string} userId - Current user ID
   * @returns {Promise<{linkToken: string}>}
   */
  async createPlaidLinkToken(userId) {
    return api.post('/plaid/link-token', { userId });
  },

  /**
   * Exchange a Plaid public_token for a stored access token (backend-side)
   * and persist the linked accounts. Returns the created accounts.
   * @param {string} userId - Current user ID
   * @param {string} publicToken - Plaid public_token from Link onSuccess
   * @param {Object} institution - { institutionId?, institutionName? }
   * @returns {Promise<{accounts: import("../interface/Account").Account[]}>}
   */
  async exchangePlaidPublicToken(userId, publicToken, institution = {}) {
    return api.post('/plaid/exchange', {
      userId,
      publicToken,
      institutionId: institution.institutionId,
      institutionName: institution.institutionName,
    });
  },

  /**
   * Sync accounts/holdings/transactions for all of a user's Plaid items.
   * @param {string} userId - Current user ID
   * @returns {Promise<{accounts: import("../interface/Account").Account[]}>}
   */
  async syncPlaidAccounts(userId) {
    return api.post(`/plaid/${userId}/sync`, {});
  },

  /**
   * User-triggered refresh (backend throttles via cooldown).
   * @param {string} userId - Current user ID
   * @returns {Promise<{accounts: import("../interface/Account").Account[]}>}
   */
  async refreshPlaidAccounts(userId) {
    return api.post(`/plaid/${userId}/refresh`, {});
  },

  /**
   * Fetch statement metadata for all linked Plaid items.
   * @param {string} userId - Current user ID
   * @returns {Promise<{synced: number, itemsRequiringConsent: string[]}>}
   */
  async syncPlaidStatements(userId) {
    return api.post(`/plaid/${userId}/statements/sync`, {});
  },

  /**
   * Create an update-mode Link token to grant Statements consent for an item.
   * @param {string} itemId - Plaid item ID
   * @returns {Promise<{linkToken: string}>}
   */
  async createPlaidStatementsConsentLinkToken(itemId) {
    return api.post(`/plaid/items/${encodeURIComponent(itemId)}/statements/consent-link-token`, {});
  },

  /**
   * Download a Plaid statement PDF and save it in the browser.
   * @param {string} statementId - Plaid external statement ID
   */
  async downloadPlaidStatement(statementId) {
    const token = await ensureFreshToken();
    const resp = await fetch(
      `${API_BASE_URL}/plaid/statements/${encodeURIComponent(statementId)}/download`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} }
    );
    if (!resp.ok) throw new Error(`Statement download failed: ${resp.status}`);
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `statement-${statementId}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },
};

export default portfolioService;
